import styled from "@emotion/styled";
import React from "react";
import { PatientDocumentTypeId } from "../messages";
import { PatientTaskInstanceDocument } from "../messages/PatientTasks";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { CHECK_SVG, VISIT_TYPE_SVG } from "../utils/svgs";
import { BURGER_MENU_ICON, CAMERA_ICON } from "../utils/svgs_new";
import { AppButton } from "./Generic/AppButton";
import { AppCard, AppCardContent, AppCardHeader } from "./Generic/AppCard";
import { AppIcon } from "./Generic/AppIcon";
import { AppList, AppListItem } from "./Generic/AppList";
import { If } from "./Generic/If";

interface Props {
  documents: PatientTaskInstanceDocument[];
  hasPlanOfCare: boolean;
  planOfCareCompleted: boolean;
  onClickRegularDocument: (documentId: PatientDocumentTypeId) => void;
  onClickScannedDocument: (name: string, pdfUrl: string) => void;
  onClickPlanOfCare: () => void;
  onClickAddDocument: () => void;
}

const MissingBadge = () => {
  return (
    <AppButton color="danger" variant="soft" className="__missing-trailing">
      {translate(STRINGS.MISSING)}
    </AppButton>
  );
};

const CompletedBadge = () => {
  return <AppIcon icon={CHECK_SVG} stroke="green" size={12} />;
};

const ResubmitBadge = () => {
  return (
    <AppButton color="danger" variant="soft" className="__missing-trailing">
      {translate(STRINGS.REOPENED)}
    </AppButton>
  );
};

const PatientTaskRequiredDocumentList = (props: Props) => {
  const handleClickDocument = React.useCallback(
    (document: PatientTaskInstanceDocument): void => {
      switch (document.type) {
        case "PatientTaskInstanceDocumentRegular": {
          props.onClickRegularDocument.call(null, document.id);
          break;
        }
        case "PatientTaskInstanceDocumentScan": {
          props.onClickScannedDocument.call(null, document.name, document.pdfUrl);
          break;
        }
        case "PatientTaskInstanceDocumentRegularScanned": {
          props.onClickScannedDocument.call(null, document.name, document.pdfUrl);
          break;
        }
        default:
      }
    },
    [props.onClickRegularDocument, props.onClickScannedDocument]
  );

  const handleClickPlanOfCare = React.useCallback(() => {
    if (props.hasPlanOfCare) {
      props.onClickPlanOfCare.call(null);
    }
  }, [props.hasPlanOfCare, props.onClickPlanOfCare]);

  const renderedPlanOfCare = React.useMemo(() => {
    if (!props.hasPlanOfCare) {
      return null;
    }

    if (props.planOfCareCompleted) {
      return (
        <AppListItem
          key="plan-of-care"
          className="__document __completed-document"
          title={translate(STRINGS.PLAN_OF_CARE)}
          trailing={<CompletedBadge />}
          onClick={handleClickPlanOfCare}
        />
      );
    }

    return (
      <AppListItem
        key="plan-of-care"
        className="__document"
        leading={<AppIcon icon={VISIT_TYPE_SVG} size={16} />}
        title={translate(STRINGS.PLAN_OF_CARE)}
        trailing={<MissingBadge />}
        onClick={handleClickPlanOfCare}
      />
    );
  }, [handleClickPlanOfCare, props.hasPlanOfCare, props.planOfCareCompleted]);

  const completedDocuments = props.documents.filter(
    (doc) =>
      (doc.type === "PatientTaskInstanceDocumentRegular" && doc.completed) ||
      doc.type !== "PatientTaskInstanceDocumentRegular"
  );
  const missingDocuments = props.documents.filter(
    (doc) => doc.type === "PatientTaskInstanceDocumentRegular" && !doc.completed
  );

  return (
    <div className="PatientTaskRequiredDocumentList">
      <AppCard>
        <AppCardHeader>{translate(STRINGS.REQUIRED_DOCUMENTS)}</AppCardHeader>
        <AppCardContent>
          <AppList bordered={true}>
            {missingDocuments.map((document: PatientTaskInstanceDocument, docId: number) => {
              const isResubmit =
                document.type === "PatientTaskInstanceDocumentRegular" &&
                document.isOpenForResubmission;
              return (
                <AppListItem
                  key={docId.toString()}
                  className="__document"
                  leading={<AppIcon icon={BURGER_MENU_ICON} />}
                  title={document.name}
                  trailing={isResubmit ? <ResubmitBadge /> : <MissingBadge />}
                  onClick={() => handleClickDocument(document)}
                />
              );
            })}
            <If condition={!props.planOfCareCompleted}>{renderedPlanOfCare}</If>
            {completedDocuments.map((document: PatientTaskInstanceDocument, docId: number) => {
              return (
                <AppListItem
                  key={docId.toString()}
                  className="__document __completed-document"
                  title={document.name}
                  trailing={<CompletedBadge />}
                  onClick={() => handleClickDocument(document)}
                />
              );
            })}
            <If condition={props.planOfCareCompleted}>{renderedPlanOfCare}</If>
          </AppList>

          <Spacer />

          <AppButton
            color="primary"
            className="__scan-new-document-button"
            onClick={props.onClickAddDocument}
          >
            <AppIcon size={20} icon={CAMERA_ICON} />
            <span className="__scan-new-document-text">
              {translate(STRINGS.ADD_ANOTHER_DOCUMENT)}
            </span>
          </AppButton>
        </AppCardContent>
      </AppCard>
    </div>
  );
};

const Spacer = styled.div`
  height: 16px;
`;

export default PatientTaskRequiredDocumentList;
